'use client'

import { useEffect, useState, Suspense } from 'react'
import { useLocation } from 'react-router-dom'
import { createClient } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { ArrowRight, AlertCircle, CheckCircle2, Wallet, ArrowLeftRight, Building2, Calendar, TrendingUp, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

/**
 * Faltante a cobrir com movimentação (regra alinhada ao Pipefy):
 * só o saldo positivo conta. Saldo negativo antigo não entra no pedido.
 * faltante = max(0, valorNecessario - max(0, saldo))
 */
function calcularFaltanteCobertura(saldoDisponivel: number, valorNecessario: number): number {
  return Math.max(0, valorNecessario - Math.max(0, Number(saldoDisponivel) || 0))
}

const FETCH_ORCAMENTOS_MAX_TENTATIVAS = 3
const FETCH_ORCAMENTOS_BACKOFF_MS = [800, 1600, 3200]

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Timeout / rede: vale retry automático sem o usuário atualizar a página. */
function isErroCargaTransitorio(err: unknown): boolean {
  const msg = String(
    (err as { message?: string })?.message ??
      (err as { error_description?: string })?.error_description ??
      err ??
      ''
  ).toLowerCase()
  return (
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('canceling statement') ||
    msg.includes('cancelling statement') ||
    msg.includes('57014') ||
    msg.includes('network') ||
    msg.includes('fetch failed') ||
    msg.includes('failed to fetch') ||
    msg.includes('connection') ||
    msg.includes('503') ||
    msg.includes('502') ||
    msg.includes('504')
  )
}

// Tipos — granularidade N3 (subtipo): uma linha por chave_unica_v2 na view V2
interface OrcamentoTipo {
  /** chave_unica_v2 (identificador único da linha N3) */
  id: string
  chave_unica_v2: string
  /** Pipefy: `solis_tipos_despesa.pipefy_database_id` da linha do orçamento (usado nas RPCs) */
  tipo_despesa_id: string
  /** UUID interno `solis_tipos_despesa.id` (algumas URLs/automações enviam este id) */
  tipo_despesa_row_id: string | null
  /** IDs do mapa Pipefy N1/N2/N3 — o parâmetro `tipo_id` da URL pode ser o N3, não o macro */
  tipo_despesa_n1_pipefy_id: string | null
  tipo_despesa_n2_pipefy_id: string | null
  tipo_despesa_n3_pipefy_id: string | null
  /** Nome do tipo (macro) para exibição */
  tipo_despesa_nome: string
  classificacao_receita_despesa: string | null
  subtipo_despesa: string | null
  centro_custo_pipefy_id: string
  centro_custo_nome: string
  valor_saldo_disponivel: number
  /** Realizado da planilha financeiro (complementar) já descontado em valor_saldo_disponivel na view V2 */
  valor_financeiro_complementar?: number
  valor_total_orcado: number
  valor_contratado: number
}

function strId(v: unknown): string {
  if (v === null || v === undefined) return ''
  return String(v).trim()
}

/** A URL costuma mandar `tipo_id` = Pipefy macro OU id N1/N2/N3 do mapa OU UUID interno. */
function linhaCasariaTipoIdDaUrl(o: OrcamentoTipo, tipoIdUrl: string): boolean {
  const t = strId(tipoIdUrl)
  if (!t) return false
  return (
    strId(o.tipo_despesa_id) === t ||
    strId(o.tipo_despesa_row_id) === t ||
    strId(o.tipo_despesa_n1_pipefy_id) === t ||
    strId(o.tipo_despesa_n2_pipefy_id) === t ||
    strId(o.tipo_despesa_n3_pipefy_id) === t
  )
}

function rotuloOrcamentoN3(o: OrcamentoTipo): string {
  const tipo = o.tipo_despesa_nome || ''
  const sub = (o.subtipo_despesa || '').trim()
  const cls = (o.classificacao_receita_despesa || '').trim()
  if (sub && cls) return `${tipo} › ${cls} › ${sub}`
  if (sub) return `${tipo} › ${sub}`
  return tipo || sub || '(sem nome)'
}

/** Mesma URL de sempre: `tipo_id` + `area_id`. Com várias linhas N3 no mesmo tipo, usa a primeira em ordem estável de `chave_unica_v2`. */
function resolverDestino(
  orcamentos: OrcamentoTipo[],
  p: MovimentacaoParams
): OrcamentoTipo | undefined {
  const area = strId(p.area_id)
  if (!strId(p.tipo_id)) return undefined
  const candidatos = orcamentos.filter(
    (o) => linhaCasariaTipoIdDaUrl(o, p.tipo_id!) && strId(o.centro_custo_pipefy_id) === area
  )
  if (candidatos.length === 0) return undefined
  return [...candidatos].sort((a, b) =>
    a.chave_unica_v2.localeCompare(b.chave_unica_v2, 'pt-BR')
  )[0]
}

type OrigemPipeline = 'contratacoes' | 'gestao_pagamentos'

interface MovimentacaoParams {
  card_id: string | null
  area_id: string | null
  tipo_id: string | null
  ano: string | null
  valor: string | null
  sig: string | null
  /** URL: origem=gestao_pagamentos → campos mover do pipe de pagamentos na edge pipefy-atualizar-card */
  origem_pipeline: OrigemPipeline | null
}

// Componente de Header com a identidade da Solis
function SolisHeader() {
  return (
    <header className="w-full bg-[#131E29] border-b border-slate-800 shadow-sm">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-center md:justify-between">
        <div className="flex flex-col items-center md:items-start gap-1">
          <img
            src="/logo-solis.svg"
            alt="Solis Investimentos"
            className="h-8 w-auto"
          />
          <div className="flex md:hidden items-center gap-2 text-slate-400 text-xs">
            <div className="h-1.5 w-1.5 rounded-full bg-orange-500 animate-pulse"></div>
            Sistema de Orçamento
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 text-slate-400 text-sm">
          <div className="h-2 w-2 rounded-full bg-orange-500 animate-pulse"></div>
          Sistema de Orçamento
        </div>
      </div>
    </header>
  )
}

function MovimentacaoContent() {
  const location = useLocation()
  const supabase = createClient()

  const [params, setParams] = useState<MovimentacaoParams>({
    card_id: null,
    area_id: null,
    tipo_id: null,
    ano: null,
    valor: null,
    sig: null,
    origem_pipeline: null,
  })

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [overlayFadingOut, setOverlayFadingOut] = useState(false)
  const [overlayOpacityReady, setOverlayOpacityReady] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Tentativa atual de carga (1..N); >1 só durante retry automático. */
  const [tentativaCarga, setTentativaCarga] = useState(1)
  
  const [orcamentos, setOrcamentos] = useState<OrcamentoTipo[]>([])
  const [tipoOrigemId, setTipoOrigemId] = useState<string>('')
  const [valorMovimentar, setValorMovimentar] = useState<number>(0)
  const [valorNecessario, setValorNecessario] = useState<number>(0)
  const [valorMovimentarInput, setValorMovimentarInput] = useState<string>('0,00')
  /** Valor efetivamente movimentado na última operação concluída (para exibir na tela de sucesso; evita ser sobrescrito pelo useEffect que zera o gap) */
  const [valorMovimentadoConfirmado, setValorMovimentadoConfirmado] = useState<number>(0)
  const [areaNome, setAreaNome] = useState<string>('')
  const [partialSuccessMessage, setPartialSuccessMessage] = useState<{ type: 'ainda_falta' | 'limite_atingido'; restante: number } | null>(null)

  // Fluxo "Solicitar verba adicional": overlay de loading e tela final
  const [encaminhandoVerbaAdicional, setEncaminhandoVerbaAdicional] = useState(false)
  const [verbaAdicionalFadingOut, setVerbaAdicionalFadingOut] = useState(false)
  const [verbaOverlayOpacityReady, setVerbaOverlayOpacityReady] = useState(false)
  const [verbaAdicionalConcluida, setVerbaAdicionalConcluida] = useState(false)
  const [verbaAdicionalRestante, setVerbaAdicionalRestante] = useState<number>(0)
  const [verbaAdicionalDestinoNome, setVerbaAdicionalDestinoNome] = useState<string>('')
  const [verbaAdicionalDialogOpen, setVerbaAdicionalDialogOpen] = useState(false)
  const [justificativaVerbaAdicionalInput, setJustificativaVerbaAdicionalInput] = useState('')
  const [verbaAdicionalPending, setVerbaAdicionalPending] = useState<{
    restante: number
    origem: OrcamentoTipo
    destino: OrcamentoTipo
  } | null>(null)

  /** Textos da UI: usa a URL direto para acertar no 1º render (evita flash). */
  const isGestaoPagamentos =
    new URLSearchParams(location.search).get('origem')?.toLowerCase().trim() === 'gestao_pagamentos'

  const PIPEFY_ORCAMENTO_DATABASE_ID = import.meta.env.VITE_PIPEFY_ORCAMENTO_DATABASE_ID || '306808641'

  async function callPipefyEdge<T = any>(body: any): Promise<T> {
    const { data, error } = await supabase.functions.invoke('pipefy-atualizar-card', {
      body,
    })

    if (error) {
      throw error
    }

    return data as T
  }

  async function atualizarOrcamentoEspelhoPipefy(
    chavesUnicas: string[] | null | undefined,
    chavesUnicasV2?: string[] | null | undefined
  ) {
    const useV2 = Array.isArray(chavesUnicasV2) && chavesUnicasV2.filter(Boolean).length > 0
    const keys = useV2 ? chavesUnicasV2!.filter(Boolean) : chavesUnicas?.filter(Boolean)
    if (!PIPEFY_ORCAMENTO_DATABASE_ID || !keys || keys.length === 0) {
      return
    }

    try {
      const { data: edgeData, error: edgeError } = await supabase.functions.invoke('update-orcamento-pipefy', {
        body: {
          orcamentoDatabaseId: PIPEFY_ORCAMENTO_DATABASE_ID,
          chavesUnicas: keys,
          mode: useV2 ? 'v2' : 'legacy',
        },
      })

      if (edgeError) {
        throw edgeError
      }

      if (params.card_id) {
        await supabase.rpc('solis_atualizar_response_movimentacao', {
          p_pipefy_card_id: params.card_id,
          p_response_json: edgeData,
        })
      }
    } catch (e) {
      console.error('Erro ao atualizar orçamento espelho no Pipefy ou salvar response da movimentação.', e)
      // Não bloqueia o fluxo da tela se apenas a atualização do espelho falhar.
    }
  }

  async function registrarMensagemMovimentacao(
    restanteApos: number,
    origem?: OrcamentoTipo,
    destino?: OrcamentoTipo | null,
    modo?: 'saldo' | 'verba_adicional',
    justificativaVerbaAdicional?: string
  ) {
    if (!params.card_id || !origem || !destino) {
      return
    }

    try {
      const agora = new Date()
      const dataHora = agora.toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })

      const linha = [
        `[${dataHora}] Movimentação de orçamento (N3)`,
        `Origem: ${rotuloOrcamentoN3(origem)}`,
        `Destino: ${rotuloOrcamentoN3(destino)}`,
        `Valor: ${formatCurrency(valorMovimentar)}`,
        `Novo saldo origem: ${formatCurrency((origem.valor_saldo_disponivel ?? 0) - valorMovimentar)}`,
        `Novo saldo destino: ${formatCurrency((destino.valor_saldo_disponivel ?? 0) + valorMovimentar)}`,
        params.origem_pipeline === 'gestao_pagamentos'
          ? `Novo valor restante para cobrir o pagamento: ${formatCurrency(restanteApos)}`
          : `Novo valor restante para cobrir a contratação: ${formatCurrency(restanteApos)}`,
        // Marcador ASCII estável para condição "contém" no Pipefy (evita NBSP do R$ 0,00)
        ...(restanteApos === 0 ? ['COBERTURA_OK'] : []),
      ].join(' | ')

      const payload: Record<string, unknown> = {
        action: 'append_log_and_maybe_update_flow',
        card_id: params.card_id,
        linha,
        mode: modo,
      }
      if (params.origem_pipeline === 'gestao_pagamentos') {
        payload.origem_pipeline = 'gestao_pagamentos'
      }
      if (modo === 'verba_adicional' && justificativaVerbaAdicional?.trim()) {
        payload.justificativa_verba_adicional = justificativaVerbaAdicional.trim()
      }

      await callPipefyEdge<{ mensagem: string }>(payload)
    } catch (e) {
      console.error('Falha ao registrar mensagem de movimentação no Pipefy.', e)
      if (modo === 'verba_adicional') throw e
    }
  }

  // Carregar parâmetros e dados iniciais
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    const card_id = searchParams.get('card_id')?.trim() ?? null
    const area_id = searchParams.get('area_id')?.trim() ?? null
    const tipo_id = searchParams.get('tipo_id')?.trim() ?? null
    const ano = searchParams.get('ano')?.trim() ?? null
    const valor = searchParams.get('valor')?.trim() ?? null
    const sig = searchParams.get('sig')
    const origemRaw = (searchParams.get('origem') || '').toLowerCase().trim()
    const origem_pipeline: OrigemPipeline | null =
      origemRaw === 'gestao_pagamentos' ? 'gestao_pagamentos' : null

    setParams({ card_id, area_id, tipo_id, ano, valor, sig, origem_pipeline })

    if (valor) {
      // Tenta converter o valor da URL para number
      // Suporta formato americano (900000.00) ou brasileiro (900.000,00)
      let valorNum = 0
      if (valor.includes(',')) {
        valorNum = parseFloat(valor.replace(/\./g, '').replace(',', '.'))
      } else {
        valorNum = parseFloat(valor)
      }
      setValorNecessario(valorNum)
    }

    if (area_id && ano) {
      fetchOrcamentos(area_id, parseInt(ano, 10), tipo_id)
    } else {
      setLoading(false)
      setError('Parâmetros inválidos na URL (área ou ano faltando).')
    }
  }, [location.search])

  // Valor padrão a movimentar = faltante da regra nova (ignora saldo negativo)
  useEffect(() => {
    if (!valorNecessario || orcamentos.length === 0) return
    if (!params.tipo_id) return
    // Após movimentação parcial o restante já foi definido no handler
    if (partialSuccessMessage) return

    const destino = resolverDestino(orcamentos, params)
    if (!destino) return

    const saldoDestino = Number(destino.valor_saldo_disponivel || 0)
    const gap = calcularFaltanteCobertura(saldoDestino, valorNecessario)

    setValorMovimentar(gap)
    setValorMovimentarInput(formatarValorBR(gap))
  }, [valorNecessario, params.tipo_id, orcamentos, partialSuccessMessage])

  // Fade-in do overlay de "Realizando movimentação..."
  useEffect(() => {
    if (submitting && !overlayFadingOut) {
      setOverlayOpacityReady(false)
      const t = setTimeout(() => setOverlayOpacityReady(true), 20)
      return () => clearTimeout(t)
    }
    if (!submitting && !overlayFadingOut) setOverlayOpacityReady(false)
  }, [submitting, overlayFadingOut])

  // Fade-in do overlay de "Encaminhando solicitação..."
  useEffect(() => {
    if (encaminhandoVerbaAdicional && !verbaAdicionalFadingOut) {
      setVerbaOverlayOpacityReady(false)
      const t = setTimeout(() => setVerbaOverlayOpacityReady(true), 20)
      return () => clearTimeout(t)
    }
    if (!encaminhandoVerbaAdicional && !verbaAdicionalFadingOut) setVerbaOverlayOpacityReady(false)
  }, [encaminhandoVerbaAdicional, verbaAdicionalFadingOut])

  // Quando o usuário seleciona uma origem cujo saldo é menor que o valor a movimentar,
  // ajustar automaticamente o valor para o máximo disponível naquela origem.
  useEffect(() => {
    if (!tipoOrigemId) return
    const origem = orcamentos.find(o => o.id === tipoOrigemId)
    if (!origem) return

    if (valorMovimentar > origem.valor_saldo_disponivel) {
      const novoValor = Math.max(0, origem.valor_saldo_disponivel)
      setValorMovimentar(novoValor)
      setValorMovimentarInput(formatarValorBR(novoValor))
    }
  }, [tipoOrigemId])

  async function fetchOrcamentos(areaId: string, ano: number, tipoIdUrl?: string | null) {
    setLoading(true)
    setError(null)
    setTentativaCarga(1)

    let ultimoErro: unknown = null

    for (let tentativa = 1; tentativa <= FETCH_ORCAMENTOS_MAX_TENTATIVAS; tentativa++) {
      setTentativaCarga(tentativa)
      try {
        // 1) Buscar dados da área atual para descobrir a diretoria
        const { data: centroAtual, error: erroCentro } = await supabase
          .from('solis_centros_custo')
          .select('pipefy_database_id, nome, diretoria_nome, ativo')
          .eq('pipefy_database_id', areaId)
          .maybeSingle()

        if (erroCentro) throw erroCentro
        if (!centroAtual) {
          throw new Error('Centro de custo não encontrado para a área informada.')
        }

        const diretoriaChave = centroAtual.diretoria_nome || centroAtual.nome

        // 2) Buscar todos os centros da mesma diretoria (área atual + áreas-irmãs)
        const { data: centrosDiretoria, error: erroCentrosDiretoria } = await supabase
          .from('solis_centros_custo')
          .select('pipefy_database_id, nome, diretoria_nome, ativo')
          .eq('diretoria_nome', diretoriaChave)

        if (erroCentrosDiretoria) throw erroCentrosDiretoria

        const centrosFiltrados = (centrosDiretoria || []).filter(c => c.ativo !== false)
        const centroIdsPipefy = centrosFiltrados.map(c => c.pipefy_database_id).filter(Boolean)

        // Garantir que pelo menos a área atual esteja incluída
        if (!centroIdsPipefy.includes(areaId)) {
          centroIdsPipefy.push(areaId)
        }

        // 3) View V2: uma linha por subtipo (N3) para todas as áreas da diretoria
        const { data, error: erroView } = await supabase
          .from('solis_vw_orcamento_por_subtipo_centro_com_chave_v2')
          .select('*')
          .in('centro_custo_pipefy_id', centroIdsPipefy)
          .eq('ano', ano)

        if (erroView) throw erroView

        if (data && data.length > 0) {
          const linhas: OrcamentoTipo[] = data.map((item: Record<string, unknown>) => {
            const tipoId = strId(item.tipo_despesa_pipefy_id)
            const centroIdPipefy = strId(item.centro_custo_pipefy_id)
            const chave = String(item.chave_unica_v2 ?? '')
            const centroNome =
              (item.centro_resultado as string) ??
              (centrosFiltrados.find((c) => strId(c.pipefy_database_id) === centroIdPipefy)?.nome ?? '')

            return {
              id: chave,
              chave_unica_v2: chave,
              tipo_despesa_id: tipoId,
              tipo_despesa_row_id: item.tipo_despesa_id != null ? strId(item.tipo_despesa_id) : null,
              tipo_despesa_n1_pipefy_id: item.tipo_despesa_n1_pipefy_id != null ? strId(item.tipo_despesa_n1_pipefy_id) : null,
              tipo_despesa_n2_pipefy_id: item.tipo_despesa_n2_pipefy_id != null ? strId(item.tipo_despesa_n2_pipefy_id) : null,
              tipo_despesa_n3_pipefy_id: item.tipo_despesa_n3_pipefy_id != null ? strId(item.tipo_despesa_n3_pipefy_id) : null,
              tipo_despesa_nome: (item.tipo_receita_despesa as string) ?? '',
              classificacao_receita_despesa: (item.classificacao_receita_despesa as string) ?? null,
              subtipo_despesa: (item.subtipo_despesa as string) ?? null,
              centro_custo_pipefy_id: centroIdPipefy,
              centro_custo_nome: centroNome || 'Área Desconhecida',
              valor_saldo_disponivel: Number(item.valor_saldo_disponivel || 0),
              valor_financeiro_complementar: Number(item.valor_financeiro_complementar || 0),
              valor_total_orcado: Number(item.valor_total_orcado || 0),
              valor_contratado: Number(item.valor_contratado || 0),
            }
          })

          setOrcamentos(linhas)

          const destinoCentro = resolverDestino(linhas, {
            card_id: null,
            area_id: areaId,
            tipo_id: tipoIdUrl ?? null,
            ano: String(ano),
            valor: null,
            sig: null,
            origem_pipeline: null,
          })
          setAreaNome(
            destinoCentro?.centro_custo_nome ??
              centroAtual.nome ??
              (data[0] as { centro_resultado?: string }).centro_resultado ??
              'Área Desconhecida'
          )

          if (tipoIdUrl && !destinoCentro) {
            const linhasNoCentroDaUrl = linhas.filter(
              (o) => strId(o.centro_custo_pipefy_id) === strId(areaId)
            )
            if (linhasNoCentroDaUrl.length === 0) {
              setError(
                `Sem linhas de orçamento em ${ano} para este centro (area_id ${areaId}). ` +
                  'Há orçamento na mesma diretoria em outros centros — inclua o orçamento deste centro na base ou corrija o area_id no link do Pipefy.'
              )
            } else {
              setError(
                'Nenhuma linha N3 deste centro combina com o tipo_id da URL (macro, UUID interno ou IDs N1/N2/N3 Pipefy).'
              )
            }
          }

          setLoading(false)
          return
        }

        // Lista vazia é resposta válida — não faz sentido retry
        setError('Nenhum orçamento encontrado para esta área/diretoria e ano.')
        setLoading(false)
        return
      } catch (err: unknown) {
        ultimoErro = err
        console.error(`Erro ao buscar orçamentos (tentativa ${tentativa}/${FETCH_ORCAMENTOS_MAX_TENTATIVAS}):`, err)

        const podeRetry =
          tentativa < FETCH_ORCAMENTOS_MAX_TENTATIVAS && isErroCargaTransitorio(err)
        if (!podeRetry) break

        const espera = FETCH_ORCAMENTOS_BACKOFF_MS[tentativa - 1] ?? 2000
        await sleep(espera)
      }
    }

    const msg =
      (ultimoErro as { message?: string })?.message ||
      'Não foi possível carregar o orçamento.'
    const sufixoRetry = isErroCargaTransitorio(ultimoErro)
      ? ' Tentamos novamente automaticamente sem sucesso. Aguarde um momento e recarregue a página.'
      : ''
    setError(`Erro ao carregar dados: ${msg}.${sufixoRetry}`)
    setLoading(false)
  }

  async function handleConfirmar() {
    setPartialSuccessMessage(null)

    if (!tipoOrigemId) {
      toast.error('Selecione uma linha de origem (N3).')
      return
    }

    if (valorMovimentar <= 0) {
      toast.error('O valor da movimentação deve ser maior que zero.')
      return
    }

    const origem = orcamentos.find(o => o.id === tipoOrigemId)
    if (origem && origem.valor_saldo_disponivel < valorMovimentar) {
      toast.error(`Saldo insuficiente em ${rotuloOrcamentoN3(origem)}. Disponível: ${formatCurrency(origem.valor_saldo_disponivel)}`)
      return
    }

    let inicioProcesso: number | null = null
    let mostrarSucessoAposFade = false

    try {
      setSubmitting(true)
      setError(null)
      inicioProcesso = Date.now()

      if (!origem || !tipoDestino || !params.area_id) {
        throw new Error('Dados de origem ou destino inválidos.')
      }

      const mesmoCentroDaArea =
        strId(origem.centro_custo_pipefy_id) === strId(params.area_id) &&
        strId(tipoDestino.centro_custo_pipefy_id) === strId(params.area_id)

      let data: { success?: boolean; error?: string; data?: { chaves_unicas?: string[]; chaves_unicas_v2?: string[] } } | null =
        null
      let error: unknown = null

      const anoNum = parseInt(params.ano || String(new Date().getFullYear()), 10)
      const clsOrig = origem.classificacao_receita_despesa ?? ''
      const subOrig = origem.subtipo_despesa ?? ''
      const clsDest = tipoDestino.classificacao_receita_despesa ?? ''
      const subDest = tipoDestino.subtipo_despesa ?? ''

      if (mesmoCentroDaArea) {
        const payload = {
          p_pipefy_card_id: params.card_id,
          p_tipo_despesa_origem_id: origem.tipo_despesa_id,
          p_tipo_despesa_destino_id: tipoDestino.tipo_despesa_id,
          p_centro_custo_id: params.area_id,
          p_ano: anoNum,
          p_valor: valorMovimentar,
          p_classificacao_origem: clsOrig,
          p_subtipo_origem: subOrig,
          p_classificacao_destino: clsDest,
          p_subtipo_destino: subDest,
          p_status: 'autorizado',
        }

        const resp = await supabase.rpc('solis_validar_e_inserir_movimentacao_n3', payload)
        data = resp.data as typeof data
        error = resp.error
      } else {
        const payloadAreas = {
          p_pipefy_card_id: params.card_id,
          p_centro_custo_origem_id: origem.centro_custo_pipefy_id,
          p_tipo_despesa_origem_id: origem.tipo_despesa_id,
          p_centro_custo_destino_id: tipoDestino.centro_custo_pipefy_id,
          p_tipo_despesa_destino_id: tipoDestino.tipo_despesa_id,
          p_ano: anoNum,
          p_valor: valorMovimentar,
          p_classificacao_origem: clsOrig,
          p_subtipo_origem: subOrig,
          p_classificacao_destino: clsDest,
          p_subtipo_destino: subDest,
          p_mes: null,
          p_status: 'autorizado',
        }

        const resp = await supabase.rpc('solis_validar_e_inserir_movimentacao_areas_irmas_n3', payloadAreas)
        data = resp.data as typeof data
        error = resp.error
      }

      if (error) throw error

      if (data && data.success === false) {
        throw new Error(data.error || 'Erro desconhecido na movimentação')
      }

      if (data?.data?.chaves_unicas_v2?.length || data?.data?.chaves_unicas?.length) {
        await atualizarOrcamentoEspelhoPipefy(data.data?.chaves_unicas, data.data?.chaves_unicas_v2)
      }

      await fetchOrcamentos(params.area_id!, parseInt(params.ano!, 10), params.tipo_id)

      const saldoDestinoAtual = tipoDestino?.valor_saldo_disponivel ?? 0
      const newSaldoOrigem = (origem?.valor_saldo_disponivel ?? 0) - valorMovimentar
      // Cobertura desta contratação/pagamento: abate o que acabou de ser movimentado do faltante
      // (não exige zerar rombo antigo da linha).
      const faltanteAntes = calcularFaltanteCobertura(saldoDestinoAtual, valorNecessario)
      const restante = Math.max(0, faltanteAntes - valorMovimentar)

      // Registrar log da movimentação no campo "mensagem" do card do Pipefy
      await registrarMensagemMovimentacao(restante, origem, tipoDestino, restante === 0 ? 'saldo' : undefined)

      if (restante > 0) {
        const saldoOrigemApos = newSaldoOrigem
        const outrosSaldos = opcoesOrigem.filter((o) => o.id !== tipoOrigemId).map((o) => o.valor_saldo_disponivel)
        const totalDisponivelOrigens = saldoOrigemApos + outrosSaldos.reduce((a, b) => a + b, 0)
        const maxSaldoUmaOrigem = Math.max(saldoOrigemApos, ...outrosSaldos, 0)

        if (totalDisponivelOrigens < restante) {
          setPartialSuccessMessage({ type: 'limite_atingido', restante })
        } else {
          setPartialSuccessMessage({ type: 'ainda_falta', restante })
        }

        const nextValor = Math.min(restante, maxSaldoUmaOrigem)
        setValorMovimentar(nextValor)
        setValorMovimentarInput(formatarValorBR(nextValor))
        setTipoOrigemId('')
        toast.success('Movimentação realizada. Realize outra movimentação para cobrir o restante.')
      } else {
        setValorMovimentadoConfirmado(valorMovimentar)
        mostrarSucessoAposFade = true
        toast.success('Movimentação realizada com sucesso!')
      }
    } catch (err: any) {
      console.error('Erro na movimentação:', err)
      setError(err.message || 'Ocorreu um erro ao processar a movimentação.')
      toast.error('Falha ao realizar movimentação.')
    } finally {
      // Garantir um tempo mínimo de feedback visual (1.5s)
      if (inicioProcesso !== null) {
        const decorrido = Date.now() - inicioProcesso
        const restante = 1500 - decorrido
        if (restante > 0) {
          await new Promise((resolve) => setTimeout(resolve, restante))
        }
      }
      setSubmitting(false)
      setOverlayFadingOut(true)
      setTimeout(() => {
        setOverlayFadingOut(false)
        if (mostrarSucessoAposFade) setSuccess(true)
      }, 350)
    }
  }

  // Helpers de formatação
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val)
  }

  // Formato Real Brasil: 1.234.567,89 (ponto milhar, vírgula decimal)
  const formatarValorBR = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }).format(val)
  }

  const handleValorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const digits = raw.replace(/\D/g, '')
    const num = digits ? Number(digits) / 100 : 0

    setValorMovimentar(num)
    setValorMovimentarInput(formatarValorBR(num))
  }

  const tipoDestino = resolverDestino(orcamentos, params)

  /** Origem: qualquer linha N3 exceto a mesma chave do destino (pode repetir tipo macro em outra linha/área). */
  const opcoesOrigem = orcamentos.filter(
    (o) => tipoDestino == null || o.chave_unica_v2 !== tipoDestino.chave_unica_v2
  )

  const tipoOrigemSelecionado = orcamentos.find(o => o.id === tipoOrigemId)

  // Mostrar "Limite atingido" só com dados carregados — evita falso positivo após timeout
  const restanteAtual =
    partialSuccessMessage?.restante ??
    calcularFaltanteCobertura(tipoDestino?.valor_saldo_disponivel ?? 0, valorNecessario)
  const totalDisponivelOrigens = opcoesOrigem.reduce((s, o) => s + (o.valor_saldo_disponivel ?? 0), 0)
  const mostrarLimiteAtingido =
    !error &&
    orcamentos.length > 0 &&
    !!tipoDestino &&
    restanteAtual > 0 &&
    totalDisponivelOrigens === 0

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <SolisHeader />
        <div className="flex items-center justify-center p-4 pt-20">
          <Card className="w-full max-w-lg border-0 shadow-lg">
            <CardHeader>
              <Skeleton className="h-8 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              {tentativaCarga > 1 && (
                <p className="text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Conexão lenta — tentando novamente ({tentativaCarga}/{FETCH_ORCAMENTOS_MAX_TENTATIVAS})…
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Tela final: solicitação de verba adicional encaminhada
  if (verbaAdicionalConcluida) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <img src="/logo-solis.svg" alt="Solis" className="h-9 w-auto opacity-90 mb-8" />
        <div className="w-full max-w-lg text-center space-y-6">
          <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4 ring-4 ring-green-50/50">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-[#131E29]">Solicitação de verba adicional encaminhada</h1>
          <p className="text-slate-600 leading-relaxed">
            Foi registrada a solicitação de verba adicional de <strong>{formatCurrency(verbaAdicionalRestante)}</strong> para{' '}
            {isGestaoPagamentos ? (
              <>cobrir o pagamento no tipo de despesa <strong>{verbaAdicionalDestinoNome}</strong>.</>
            ) : (
              <>cobrir a contratação em <strong>{verbaAdicionalDestinoNome}</strong>.</>
            )}
          </p>
          <p className="text-slate-600 leading-relaxed">
            O card no Pipefy foi atualizado e já foi encaminhado para aprovação do conselho gestor. Você pode acompanhar o andamento pelo próprio Pipefy.
          </p>
          <p className="text-slate-500 text-sm pt-4">
            Você pode fechar esta janela.
          </p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <SolisHeader />
        <div className="flex items-center justify-center p-4 pt-20">
          <Card className="w-full max-w-lg border-0 shadow-xl overflow-hidden">
            <div className="h-2 w-full bg-gradient-to-r from-green-500 to-emerald-600"></div>
            <CardHeader className="text-center pb-2 pt-8">
              <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4 ring-4 ring-green-50/50">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-slate-800">Movimentação Confirmada</CardTitle>
              <CardDescription className="text-slate-500">
                O saldo foi transferido com sucesso e o orçamento atualizado.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6 px-8 pb-8">
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-100 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-slate-500 uppercase tracking-wide">Valor Movimentado</span>
                  <span className="font-bold text-xl text-slate-800">{formatCurrency(valorMovimentadoConfirmado)}</span>
                </div>
                <Separator className="bg-slate-200" />
                <div className="grid grid-cols-2 gap-6 text-sm">
                  <div>
                    <p className="text-slate-400 text-xs uppercase font-bold mb-1">Origem</p>
                    <p className="font-semibold text-slate-700 leading-tight mb-1">
                      {tipoOrigemSelecionado ? rotuloOrcamentoN3(tipoOrigemSelecionado) : ''}
                    </p>
                    <p className="text-xs text-slate-500">Novo Saldo: <span className="text-slate-700 font-medium">{formatCurrency(tipoOrigemSelecionado?.valor_saldo_disponivel || 0)}</span></p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-xs uppercase font-bold mb-1">Destino</p>
                    <p className="font-semibold text-slate-700 leading-tight mb-1">
                      {tipoDestino ? rotuloOrcamentoN3(tipoDestino) : ''}
                    </p>
                    <p className="text-xs text-slate-500">Novo Saldo: <span className="text-green-600 font-bold">{formatCurrency(tipoDestino?.valor_saldo_disponivel || 0)}</span></p>
                  </div>
                </div>
              </div>
              
              <Alert className="bg-blue-50 border-blue-100 text-blue-900">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800 font-semibold">Próximos passos</AlertTitle>
                <AlertDescription className="text-blue-700/80">
                  Você pode fechar esta janela. O card no Pipefy foi atualizado com o novo saldo e já seguiu para a próxima fase
                  {isGestaoPagamentos ? ' do fluxo de pagamentos' : ''}.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const showOverlay = submitting || overlayFadingOut
  const showVerbaOverlay = encaminhandoVerbaAdicional || verbaAdicionalFadingOut
  const overlayVisible = showOverlay || showVerbaOverlay
  const overlayOpacity = showVerbaOverlay
    ? (verbaAdicionalFadingOut ? 0 : verbaOverlayOpacityReady ? 1 : 0)
    : (overlayFadingOut ? 0 : overlayOpacityReady ? 1 : 0)

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Tela branca com loading (cores Solis) – fade in / fade out */}
      {overlayVisible && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-8 bg-white transition-opacity duration-300 ease-out"
          style={{ opacity: overlayOpacity }}
          aria-live="polite"
          aria-busy="true"
        >
          <img
            src="/logo-solis.svg"
            alt="Solis"
            className="h-9 w-auto opacity-90"
          />
          <div className="flex flex-col items-center gap-4">
            <div
              className="h-12 w-12 rounded-full border-4 border-[#131E29]/20 border-t-orange-500 border-r-[#131E29] animate-spin"
              aria-hidden
            />
            <p className="text-[#131E29] font-semibold text-lg">
              {showVerbaOverlay ? 'Encaminhando solicitação...' : 'Realizando movimentação...'}
            </p>
          </div>
        </div>
      )}

      <Dialog
        open={verbaAdicionalDialogOpen}
        onOpenChange={(open) => {
          setVerbaAdicionalDialogOpen(open)
          if (!open) {
            setVerbaAdicionalPending(null)
            setJustificativaVerbaAdicionalInput('')
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Solicitar verba adicional</DialogTitle>
            <DialogDescription>
              Preencha a justificativa abaixo. Ela será gravada no card do Pipefy antes de encaminhar o fluxo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="justificativa-verba-adicional">Justicativa verba adicional</Label>
            <Textarea
              id="justificativa-verba-adicional"
              rows={6}
              value={justificativaVerbaAdicionalInput}
              onChange={(e) => setJustificativaVerbaAdicionalInput(e.target.value)}
              placeholder="Descreva o motivo da necessidade de verba adicional..."
              className="min-h-[140px] resize-y"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setVerbaAdicionalDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={async () => {
                const trimmed = justificativaVerbaAdicionalInput.trim()
                if (!trimmed) {
                  toast.error('Preencha a justificativa para solicitar verba adicional.')
                  return
                }
                const p = verbaAdicionalPending
                if (!p) return
                setVerbaAdicionalDialogOpen(false)
                setVerbaAdicionalPending(null)
                setJustificativaVerbaAdicionalInput('')
                setVerbaAdicionalRestante(p.restante)
                setVerbaAdicionalDestinoNome(rotuloOrcamentoN3(p.destino))
                setEncaminhandoVerbaAdicional(true)
                const inicio = Date.now()
                try {
                  await registrarMensagemMovimentacao(
                    p.restante,
                    p.origem,
                    p.destino,
                    'verba_adicional',
                    trimmed
                  )
                  const decorrido = Date.now() - inicio
                  if (decorrido < 1500) await new Promise((r) => setTimeout(r, 1500 - decorrido))
                } catch (e) {
                  console.error('Erro ao encaminhar solicitação de verba adicional', e)
                  toast.error('Não foi possível encaminhar. Tente novamente.')
                  setEncaminhandoVerbaAdicional(false)
                  return
                }
                setEncaminhandoVerbaAdicional(false)
                setVerbaAdicionalFadingOut(true)
                setTimeout(() => {
                  setVerbaAdicionalFadingOut(false)
                  setVerbaAdicionalConcluida(true)
                }, 350)
              }}
            >
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SolisHeader />
      
      <div className="flex justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl space-y-8">
        
        {/* Header */}
          <div className="text-center space-y-2 mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-[#131E29]">Movimentação de Orçamento</h1>
            <p className="text-slate-500 max-w-2xl mx-auto">
              {isGestaoPagamentos
                ? 'Gestão de pagamentos: remanejamento de saldo entre linhas de orçamento (subtipo N3) e áreas para cobrir o valor do pagamento.'
                : 'Remanejamento de saldo entre centros de custo e linhas de orçamento (nível N3).'}
            </p>
          </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Erro</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {(partialSuccessMessage || mostrarLimiteAtingido) && (
          <Alert className="mb-6 border-2 border-orange-200 bg-orange-50 text-orange-900">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertTitle className="font-semibold text-orange-800">
              {(partialSuccessMessage?.type === 'limite_atingido' || mostrarLimiteAtingido) ? 'Limite de movimentações atingido' : 'Saldo ainda insuficiente'}
            </AlertTitle>
            <AlertDescription className="text-orange-800/90 mt-1">
              {(partialSuccessMessage?.type === 'limite_atingido' || mostrarLimiteAtingido) ? (
                <>
                  Mesmo utilizando todo o saldo disponível dos outros tipos de despesa desta área, ainda faltam <strong>{formatCurrency(partialSuccessMessage?.restante ?? restanteAtual)}</strong> para{' '}
                  {isGestaoPagamentos ? (
                    <>cobrir este pagamento. Solicite verba adicional ou ajuste o valor no Pipefy.</>
                  ) : (
                    <>cobrir a contratação. Para prosseguir, será necessário solicitar verba adicional ou ajustar o valor da contratação.</>
                  )}
                </>
              ) : (
                <>
                  Movimentação realizada. Ainda faltam <strong>{formatCurrency(partialSuccessMessage!.restante)}</strong> para{' '}
                  {isGestaoPagamentos ? (
                    <>cobrir totalmente este pagamento. Realize outra movimentação ou solicite verba adicional.</>
                  ) : (
                    <>cobrir totalmente a contratação. Realize outra movimentação de saldo abaixo ou opte por solicitar verba adicional.</>
                  )}
                </>
              )}
              {(partialSuccessMessage?.type === 'limite_atingido' || mostrarLimiteAtingido) && (
                <div className="mt-3">
                  <Button
                    type="button"
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => {
                      const origem = tipoOrigemSelecionado ?? opcoesOrigem[0]
                      const restante = partialSuccessMessage?.restante ?? restanteAtual
                      if (!origem || !tipoDestino) return
                      setVerbaAdicionalPending({ restante, origem, destino: tipoDestino })
                      setJustificativaVerbaAdicionalInput('')
                      setVerbaAdicionalDialogOpen(true)
                    }}
                  >
                    Solicitar verba adicional
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {/* Coluna da Esquerda: Contexto */}
          <div className="space-y-6">
            <Card className="border-0 shadow-md overflow-hidden">
              <div className="h-1 w-full bg-slate-200"></div>
              <CardHeader className="pb-4 bg-white">
                <CardTitle className="text-lg flex items-center gap-2 text-[#131E29]">
                  <Building2 className="w-5 h-5 text-orange-500" />
                  Contexto da Solicitação
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Área / Centro de Custo</Label>
                    <div className="font-semibold text-slate-800 mt-0.5">{areaNome}</div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Ano de Referência</Label>
                    <div className="font-semibold text-slate-800 flex items-center gap-1 mt-0.5">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" /> {params.ano}
                    </div>
                  </div>
                </div>
                
                <div className="pt-2">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Linha de orçamento — destino (N3)</Label>
                  <div className="font-semibold text-[#131E29] text-lg mt-0.5 mb-2">
                    {tipoDestino ? rotuloOrcamentoN3(tipoDestino) : '(destino não identificado para tipo_id e área)'}
                  </div>
                  <div className="flex justify-between items-center bg-red-50 p-3 rounded-lg border border-red-100">
                    <span className="text-xs text-red-700 font-semibold uppercase">Saldo Atual Disponível</span>
                    <span className="text-base font-bold text-red-700">{formatCurrency(tipoDestino?.valor_saldo_disponivel || 0)}</span>
                  </div>
                  {tipoDestino && restanteAtual > 0 && (
                    <div className="flex justify-between items-center bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <span className="text-xs text-amber-800 font-semibold uppercase">
                        {isGestaoPagamentos ? 'Valor restante para o pagamento' : 'Valor restante para contratação'}
                      </span>
                      <span className="text-base font-bold text-amber-800">{formatCurrency(restanteAtual)}</span>
                    </div>
                  )}
                </div>

                <div className="pt-2 pb-2">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                    {isGestaoPagamentos ? 'Valor necessário para o pagamento' : 'Valor necessário para contratação'}
                  </Label>
                  <div className="text-3xl font-bold text-[#131E29] mt-1 tracking-tight">
                    {formatCurrency(valorNecessario || valorMovimentar)}
                  </div>
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                    <TrendingUp className="w-3 h-3" />
                    {isGestaoPagamentos
                      ? 'É o valor deste pagamento a cobrir no orçamento; você pode usar movimentações de saldo entre tipos/áreas.'
                      : 'Este é o valor total da contratação; você pode cobrir parte ou todo esse valor com movimentações de saldo.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Alert className="bg-amber-50 border-amber-200 text-amber-900 shadow-sm">
              <Wallet className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800 font-semibold">Ação Necessária</AlertTitle>
              <AlertDescription className="text-amber-800/80 text-sm mt-1">
                O saldo atual é insuficiente. Selecione de onde deseja retirar recursos para cobrir esta despesa.
              </AlertDescription>
            </Alert>
          </div>

          {/* Coluna da Direita: Ação */}
          <div className="space-y-6">
            <Card className="border-0 shadow-xl overflow-hidden ring-1 ring-slate-900/5">
              <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 to-orange-500"></div>
              <CardHeader className="bg-white pb-6 pt-6">
                <CardTitle className="text-xl flex items-center gap-2 text-[#131E29]">
                  <ArrowLeftRight className="w-5 h-5 text-orange-500" />
                  Executar Movimentação
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Configure a transferência de saldo entre contas.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-2 px-6">
                
                <div className="space-y-2">
                  <Label htmlFor="origem" className="text-slate-700 font-semibold">Retirar saldo de (Origem)</Label>
                  <Select
                    value={tipoOrigemId}
                    onValueChange={(value) => {
                      setTipoOrigemId(value)
                      setPartialSuccessMessage(null)

                      const origem = orcamentos.find(o => o.id === value)
                      const destino = tipoDestino
                      if (!origem || !destino) return

                      const saldoDestino = Number(destino.valor_saldo_disponivel || 0)
                      const gap =
                        partialSuccessMessage?.restante ??
                        calcularFaltanteCobertura(saldoDestino, valorNecessario)

                      // Se o gap for maior que o saldo da nova origem, limitar ao saldo disponível
                      const novoValor = Math.min(gap, origem.valor_saldo_disponivel)
                      setValorMovimentar(novoValor)
                      setValorMovimentarInput(formatarValorBR(novoValor))
                    }}
                  >
                    <SelectTrigger id="origem" className="w-full h-12 bg-slate-50 border-slate-200 focus:ring-orange-500/20 focus:border-orange-500">
                      <SelectValue placeholder="Selecione a linha de origem (N3)..." />
                    </SelectTrigger>
                    <SelectContent>
                      {opcoesOrigem.map((tipo) => (
                        <SelectItem 
                          key={tipo.id} 
                          value={tipo.id}
                          disabled={tipo.valor_saldo_disponivel <= 0}
                        >
                          {`[${tipo.centro_custo_nome}] ${rotuloOrcamentoN3(tipo)} — ${formatCurrency(tipo.valor_saldo_disponivel)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {tipoOrigemSelecionado && (
                    <div className="text-xs text-right text-slate-500">
                      Disponível: <span className="font-medium text-slate-900">{formatCurrency(tipoOrigemSelecionado.valor_saldo_disponivel)}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="valor" className="text-slate-700 font-semibold">Valor a Movimentar</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-slate-500 font-medium">R$</span>
                    <Input
                      id="valor"
                      type="text"
                      inputMode="decimal"
                      value={valorMovimentarInput}
                      onChange={handleValorInputChange}
                      className="pl-10 font-mono text-lg h-12 bg-slate-50 border-slate-200 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>
                </div>

                {tipoOrigemSelecionado && (
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 text-sm space-y-3">
                    <p className="font-semibold text-[#131E29] mb-1 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span> Resumo da Operação
                    </p>
                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        <span className="block">{rotuloOrcamentoN3(tipoOrigemSelecionado)}</span>
                        <span className="block text-[11px] text-slate-400">
                          {tipoOrigemSelecionado.centro_custo_nome}
                        </span>
                      </span>
                      <span className="text-red-600 font-mono">- {formatCurrency(valorMovimentar)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        <span className="block">{tipoDestino ? rotuloOrcamentoN3(tipoDestino) : ''}</span>
                        <span className="block text-[11px] text-slate-400">
                          {tipoDestino?.centro_custo_nome}
                        </span>
                      </span>
                      <span className="text-green-600 font-mono">+ {formatCurrency(valorMovimentar)}</span>
                    </div>
                    <Separator className="my-1 bg-slate-200" />
                    <div className="flex justify-between font-medium pt-1">
                      <span className="text-slate-700">Novo Saldo {rotuloOrcamentoN3(tipoOrigemSelecionado)}</span>
                      <span className={`${(tipoOrigemSelecionado.valor_saldo_disponivel - valorMovimentar) < 0 ? 'text-red-600' : 'text-[#131E29]'}`}>
                        {formatCurrency(tipoOrigemSelecionado.valor_saldo_disponivel - valorMovimentar)}
                      </span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span className="text-slate-700">Novo Saldo {tipoDestino ? rotuloOrcamentoN3(tipoDestino) : ''}</span>
                      <span className="text-green-700">
                        {formatCurrency((tipoDestino?.valor_saldo_disponivel ?? 0) + valorMovimentar)}
                      </span>
                    </div>
                    {tipoDestino &&
                      Math.max(
                        0,
                        calcularFaltanteCobertura(tipoDestino.valor_saldo_disponivel ?? 0, valorNecessario) -
                          valorMovimentar
                      ) > 0 && (
                      <div className="flex justify-between font-medium pt-1 text-amber-700 bg-amber-50 -mx-1 px-2 py-1.5 rounded mt-1">
                        <span>
                          {isGestaoPagamentos
                            ? 'Novo valor restante para cobrir o pagamento'
                            : 'Novo valor restante para cobrir a contratação'}
                        </span>
                        <span className="font-mono">
                          {formatCurrency(
                            Math.max(
                              0,
                              calcularFaltanteCobertura(tipoDestino.valor_saldo_disponivel ?? 0, valorNecessario) -
                                valorMovimentar
                            )
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}

              </CardContent>
              <CardFooter className="bg-slate-50/50 border-t border-slate-100 pt-6 pb-6 px-6">
                <Button 
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold shadow-md hover:shadow-lg transition-all duration-200 h-12 text-base border-0" 
                  size="lg"
                  onClick={handleConfirmar}
                  disabled={
                    submitting || 
                    !tipoOrigemId || 
                    valorMovimentar <= 0 || 
                    (tipoOrigemSelecionado ? tipoOrigemSelecionado.valor_saldo_disponivel < valorMovimentar : false)
                  }
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Realizando movimentação...</span>
                    </span>
                  ) : (
                    <>
                      Confirmar Movimentação <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>

        </div>
      </div>
    </div>
  )
}

export default function MovimentacaoPage() {
  return (
    <Suspense fallback={<div className="p-8 text-center">Carregando...</div>}>
      <MovimentacaoContent />
    </Suspense>
  )
}
